import { createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { db, debouncedWrite, reportError } from '~/lib/db';
import { monthlyIncome, totalValue } from '~/stores/portfolio-store';
import { JURISDICTIONS, TAX_YEAR, bracketFor, jurisdiction, type Bracket } from '~shared/tax-rates';

/**
 * Who the holder is, for the figures that cannot be computed without knowing.
 *
 * Not preferences — an input to the model. Province and marginal rate change
 * every after-tax number the app produces, the same way a holding changes every
 * pre-tax one, which is why this is a page of its own rather than a corner of a
 * settings screen next to the theme switch.
 *
 * Two facts, and the rates fall out: province and taxable income pick a bracket
 * out of shared/tax-rates, which is a transcribed table of combined federal and
 * provincial marginal rates. Nothing here computes a rate — the gross-up and the
 * capital gains inclusion are already inside the published figures, so applying
 * them again would be the classic double-count.
 */

export interface Profile {
    province: string | null;
    /** Taxable income before anything this app pays you — what picks the
        bracket your portfolio income stacks on top of. */
    income: number | null;
    birthYear: number | null;
    /** RRSP deduction limit, copied from the latest Notice of Assessment. */
    rrspRoom: number | null;
    /** Whether OAS clawback applies at all. Everything gated behind it is only
        asked for, and only shown, when this is true — which is what keeps the
        retirement path from leaking into an app most of whose users are still
        working. */
    retired: boolean;
    /** OAS received in a year. Caps the recovery: you cannot lose more of it
        than you are paid. */
    oasAnnual: number | null;
    /**
     * Target gross distributions per month.
     *
     * The one number that gives every other number a shape. A book yielding 7.4%
     * is a fact you cannot be pleased or disappointed by; $412 of $2,000 is a
     * position on a road.
     *
     * Here rather than on a portfolio because the goal belongs to the person and
     * the portfolios are the attempts at it — one aspiration, against which the
     * real book and every plan are measured with the same ruler.
     */
    goalMonthly: number | null;
}

export { JURISDICTIONS, TAX_YEAR };

const EMPTY: Profile = { province: null, income: null, birthYear: null, rrspRoom: null, retired: false, oasAnnual: null, goalMonthly: null };

const [profile, setProfile] = createStore<Profile>({ ...EMPTY });
const [loading, setLoading] = createSignal(true);

export { profile, loading };

export const load = async (): Promise<void> => {
    setLoading(true);
    try {
        // maybeSingle, not single: there is no row until something is set, and a
        // missing profile is the normal state for a new account rather than an
        // error to report.
        const { data, error } = await db.from('profiles').select('province, income, birth_year, rrsp_room, retired, oas_annual, goal_monthly').maybeSingle();
        if (error) throw new Error(error.message);
        setProfile(
            data == null
                ? { ...EMPTY }
                : {
                      province: data.province == null ? null : String(data.province),
                      income: data.income == null ? null : Number(data.income),
                      birthYear: data.birth_year == null ? null : Number(data.birth_year),
                      rrspRoom: data.rrsp_room == null ? null : Number(data.rrsp_room),
                      retired: data.retired === true,
                      oasAnnual: data.oas_annual == null ? null : Number(data.oas_annual),
                      goalMonthly: data.goal_monthly == null ? null : Number(data.goal_monthly),
                  },
        );
    } catch (e) {
        reportError('load profile', e);
    } finally {
        setLoading(false);
    }
};

/** Upsert on the primary key, so the first edit creates the row and every later
    one updates it — there is no create step to get wrong. */
const persist = (column: string, value: unknown): void => {
    debouncedWrite(`profile:${column}`, async () => {
        const { error } = await db.from('profiles').upsert({ [column]: value }, { onConflict: 'user_id' });
        if (error) throw new Error(error.message);
    });
};

const set = <K extends keyof Profile>(key: K, column: string, value: Profile[K]): void => {
    setProfile(key, value);
    persist(column, value);
};

export const setProvince = (code: string | null): void => set('province', 'province', code);
export const setIncome = (amount: number | null): void => set('income', 'income', amount);
export const setBirthYear = (year: number | null): void => set('birthYear', 'birth_year', year);
export const setRrspRoom = (amount: number | null): void => set('rrspRoom', 'rrsp_room', amount);
export const setRetired = (on: boolean): void => set('retired', 'retired', on);
export const setOasAnnual = (amount: number | null): void => set('oasAnnual', 'oas_annual', amount);
export const setGoalMonthly = (amount: number | null): void => set('goalMonthly', 'goal_monthly', amount);

/**
 * How far along the book is, in percent — or null when there is no goal or no
 * priced income to measure.
 *
 * Uncapped on purpose. Passing the goal is the whole point of having one, and
 * clamping to 100% would take away the only moment it exists to show you.
 */
export const goalProgress = (): number | null => {
    const goal = profile.goalMonthly;
    const now = monthlyIncome();
    return goal == null || goal <= 0 || now == null ? null : (now / goal) * 100;
};

/** What is still missing, per month. Zero once the goal is met — never negative,
    because "you are $180 short" and "you are $180 over" are different sentences
    and the second is `goalProgress`'s to tell. */
export const goalShortfall = (): number | null => {
    const goal = profile.goalMonthly;
    const now = monthlyIncome();
    return goal == null || goal <= 0 || now == null ? null : Math.max(0, goal - now);
};

/**
 * The yield the capital estimate assumes.
 *
 * A round number, and a constant rather than a field, because the estimate it
 * feeds is a sense of scale rather than a plan: "that is a quarter of a million
 * dollars" is the useful output, and it does not become more useful for being
 * tuned to 9.6%. It sits about mid-pool — these funds run roughly 7% to 13% —
 * so it flatters neither the cheapest nor the richest payer.
 *
 * Named and exported so the figure is never printed without the assumption
 * printed beside it. A capital number with no yield attached is meaningless, and
 * the reader has no way to tell which one was used.
 */
export const GOAL_YIELD = 10;

/**
 * Roughly what it takes to pay the goal, at GOAL_YIELD.
 *
 * The most-asked question this app can answer and the one nothing else on the
 * screen does: the whole book is denominated in what you have, and this is the
 * only figure denominated in what it would take.
 *
 * An estimate in the strict sense. It assumes the capital is fully invested at
 * one yield, ignores the distribution being partly return of capital — which
 * makes the same cash cost less tax but does not make it a larger cheque — and
 * ignores tax altogether, which is the gap the after-tax line beside it exists
 * to show.
 */
export const capitalRequired = (): number | null => (profile.goalMonthly == null || profile.goalMonthly <= 0 ? null : (profile.goalMonthly * 12) / (GOAL_YIELD / 100));

/**
 * How much more capital that implies, against what the book is worth today.
 *
 * Against market value rather than book cost: what a position would fetch is
 * what it can be counted on to earn from here, and what it once cost is not.
 * Zero once you are there, never negative — being past the goal is the progress
 * figure's sentence to say.
 */
export const capitalShortfall = (): number | null => {
    const need = capitalRequired();
    const have = totalValue();
    return need == null || have == null ? null : Math.max(0, need - have);
};

/**
 * What the goal is worth after tax, per month.
 *
 * The goal is gross, matching the figure the app already shows everywhere — but
 * $2,000 a month of distributions in a margin account is not $2,000 to spend,
 * and a retirement plan built on the gross number is short by the bracket.
 *
 * Charged at the interest-and-foreign-income rate, which is the right one for
 * this pool rather than a conservative choice: a covered-call ETF's distribution
 * is mostly return of capital and capital gains, and the taxable remainder is
 * largely option premium, which is taxed as ordinary income. Null unless a
 * bracket is set, like every other after-tax figure here.
 */
export const goalAfterTax = (): number | null => (profile.goalMonthly == null ? null : afterTax(profile.goalMonthly, 'other'));

/**
 * The bracket the next dollar lands in, or null until both facts are set.
 *
 * Null rather than a default province and a guessed income: a number computed
 * against a blank profile is not a conservative estimate, it is a wrong one, so
 * nothing shows an after-tax figure until this returns something.
 */
export const bracket = (): Bracket | null => (profile.province == null || profile.income == null ? null : bracketFor(profile.province, profile.income));

export const provinceName = (): string | null => (profile.province == null ? null : (jurisdiction(profile.province)?.name ?? null));

export const hasRates = (): boolean => bracket() != null;

/**
 * After-tax value of a dollar of each kind of distribution.
 *
 * The published rates already contain the 38% gross-up and the 50% capital
 * gains inclusion, so they apply to the amount actually received. Return of
 * capital is not here because it is not income: it is untaxed on receipt and
 * lowers cost base instead, surfacing later as a capital gain.
 */
export const afterTax = (amount: number, kind: 'eligible' | 'capitalGains' | 'other' | 'nonEligible'): number | null => {
    const b = bracket();
    return b == null ? null : amount * (1 - b[kind] / 100);
};

/**
 * Short form of the assumption behind any after-tax number — "ON · 43.4%".
 *
 * Meant to be printed next to the figure itself rather than left on this page.
 * A settings screen is somewhere you visit once and forget, and the failure it
 * invites is a number quietly computed against a province you moved out of a
 * year ago.
 */
export const assumption = (): string | null => {
    const b = bracket();
    return b == null ? null : `${profile.province} · ${b.other.toFixed(1)}% marginal · ${TAX_YEAR}`;
};
